import { useContext } from "react";
import { Context } from "../../../context/Context";
import { Button, Check, Select, Switch, Tags, X } from "../../ui";
import AiImportField, { ProvenanceBadge } from "./AiImportField";
import { DuplicateResolver } from "./AiImportItemCard";
import { aiImportText } from "./aiImportText";
import { formatMoney, pairText, percent } from "./aiImportModel";

// An addition group (sizes / choice / extras) with its options and the items
// it is linked to. Links are suggestions until applied: accepted ones and
// confident suggestions apply; low-confidence ones need an explicit accept.
export default function AiImportAdditionCard({ addition, items, relationships, canEdit, onOps }) {
  const { lang } = useContext(Context);
  const t = aiImportText(lang);
  const disabled = !canEdit;
  const excluded = addition.status === "exclude";
  const op = (entity, key, field) => (next) => onOps([{ ...next, entity, key, field }]);
  const links = relationships.filter((r) => r.additionKey === addition.key);
  const itemsByKey = new Map(items.map((i) => [i.key, i]));
  const linkable = items.filter((i) => i.status !== "exclude" && !links.some((l) => l.itemKey === i.key && l.status !== "rejected"));
  const issues = (addition.issues || []).filter((code) => t.issues[code]);

  return (
    <article className={`ai-import-item ai-import-addition ${excluded ? "is-excluded" : ""}`} aria-label={pairText(addition.title, lang)}>
      <header className="ai-import-item-head">
        <h4 dir="auto">{pairText(addition.title, lang)}</h4>
        <span className="ai-import-item-tags">
          <Tags variant="primary" size="sm">{t.additionKinds[addition.kind]}</Tags>
          <ProvenanceBadge field={{ provenance: addition.origin, confidence: addition.confidence, value: 1 }} />
          {!excluded && issues.map((code) => (
            <Tags key={code} variant={/UNRESOLVED|REQUIRED|INVALID/.test(code) ? "danger" : "warning"} size="sm">
              {t.issues[code]}
            </Tags>
          ))}
        </span>
        {canEdit && (
          <Button type="button" variant={excluded ? "success" : "warning"} appearance="soft" size="xs" rounded onClick={() => onOps([{ op: "setAdditionStatus", key: addition.key, status: excluded ? "include" : "exclude" }])}>
            {excluded ? t.include : t.exclude}
          </Button>
        )}
      </header>
      {!excluded && (
        <>
          <div className="ai-import-fields">
            <AiImportField label={t.fields.title + " (AR)"} field={addition.title.ar} dir="rtl" disabled={disabled} onOp={op("addition", addition.key, "title.ar")} />
            <AiImportField label={t.fields.title + " (EN)"} field={addition.title.en} dir="ltr" disabled={disabled} onOp={op("addition", addition.key, "title.en")} />
            <AiImportField label={t.fields.min} field={addition.min} type="number" disabled={disabled} onOp={op("addition", addition.key, "min")} />
            <AiImportField label={t.fields.max} field={addition.max} type="number" disabled={disabled} onOp={op("addition", addition.key, "max")} />
            <div className="ai-import-field">
              <span className="ai-import-field-label">{t.fields.mandatory}</span>
              <Switch checked={Boolean(addition.isMandatory.value)} disabled={disabled} label={addition.isMandatory.value ? "✓" : "—"} onChange={(e) => onOps([{ op: "setField", entity: "addition", key: addition.key, field: "isMandatory", value: e.target.checked }])} />
            </div>
          </div>
          <ul className="ai-import-options">
            {addition.options.map((option) => (
              <li key={option.key} className="ai-import-fields">
                <AiImportField label={t.fields.nameAr} field={option.name.ar} dir="rtl" disabled={disabled} onOp={op("option", option.key, "name.ar")} />
                <AiImportField label={t.fields.nameEn} field={option.name.en} dir="ltr" disabled={disabled} onOp={op("option", option.key, "name.en")} />
                <AiImportField label={t.fields.optionPrice} field={option.price} type="number" display={(v) => (v === null || v === undefined ? t.empty : `+${formatMoney(v, t)}`)} disabled={disabled} onOp={op("option", option.key, "price")} />
              </li>
            ))}
          </ul>
          <DuplicateResolver entity="addition" entry={addition} decisions={["create", "reuse", "ignore"]} disabled={disabled} onOp={(next) => onOps([next])} />
          <section className="ai-import-links" aria-label={t.linkedItems}>
            <div className="ai-import-field-head">
              <strong>{t.linkedItems}</strong>
              {canEdit && links.some((l) => l.status === "proposed") && (
                <span className="ai-import-field-actions">
                  <Button type="button" variant="success" appearance="soft" size="xs" rounded onClick={() => onOps([{ op: "setAllRelationships", additionKey: addition.key, status: "accepted" }])}>
                    {t.acceptAllLinks}
                  </Button>
                  <Button type="button" variant="danger" appearance="soft" size="xs" rounded onClick={() => onOps([{ op: "setAllRelationships", additionKey: addition.key, status: "rejected" }])}>
                    {t.rejectAllLinks}
                  </Button>
                </span>
              )}
            </div>
            {!links.length && <p className="menu-note">{t.noLinks}</p>}
            <ul>
              {links.map((link) => {
                const item = itemsByKey.get(link.itemKey);
                return (
                  <li key={link.key} className={`ai-import-link is-${link.status} ${link.applies ? "applies" : ""}`}>
                    <span dir="auto">{pairText(item?.name, lang)}</span>
                    <Tags variant={link.status === "accepted" ? "success" : link.status === "rejected" ? "neutral" : "info"} size="sm">
                      {t.linkStatus[link.status]}
                    </Tags>
                    {link.status !== "rejected" && <span className="menu-secondary">{link.applies ? t.linkApplies : t.linkSkipped}</span>}
                    {link.status === "proposed" && link.confidence < 0.6 && <span className="ai-import-confidence">{t.confidence(percent(link.confidence))}</span>}
                    {link.reason && <span className="menu-secondary" dir="auto">{t.linkReason}: {link.reason}</span>}
                    {canEdit && (
                      <span className="ai-import-field-actions">
                        {link.status !== "accepted" && (
                          <Button type="button" variant="success" appearance="soft" size="xs" rounded icon={<Check size={13} />} aria-label={`${t.accept}: ${pairText(item?.name, lang)}`} onClick={() => onOps([{ op: "setRelationship", itemKey: link.itemKey, additionKey: addition.key, status: "accepted" }])} />
                        )}
                        {link.status !== "rejected" && (
                          <Button type="button" variant="danger" appearance="soft" size="xs" rounded icon={<X size={13} />} aria-label={`${t.reject}: ${pairText(item?.name, lang)}`} onClick={() => onOps([{ op: "setRelationship", itemKey: link.itemKey, additionKey: addition.key, status: "rejected" }])} />
                        )}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            {canEdit && linkable.length > 0 && (
              <Select
                label={t.addLink}
                placeholder={t.selectItem}
                searchable
                fullWidth={false}
                value=""
                options={linkable.map((i) => ({ value: i.key, label: pairText(i.name, lang) }))}
                onChange={(e) => onOps([{ op: "setRelationship", itemKey: e.target.value, additionKey: addition.key, status: "accepted" }])}
              />
            )}
          </section>
        </>
      )}
    </article>
  );
}
